import React from 'react';
import {
  Button, IconButton, Menu, ConfirmDialog, Spinner,
} from '@parsina/framework';
import {
  Plus, MoreHorizontal, Pencil, Trash2, Users, Stethoscope,
} from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { BalanceLine } from '../common/ParsehCost.jsx';
import { groupThreads, dayGroupKey, relativeTimeOf } from '../../screens/Chat/Chat.shared.js';
import { threadTitle } from '../../lib/conversation.js';
import { HoverRevealStyle, HOVER_HOST, HOVER_ACTIONS } from './hoverReveal.jsx';

// The conversation list. core.md §9.1.
//
// «گفتگوی جدید» on top, this patient's threads grouped into امروز / دیروز /
// ۷ روز گذشته / قدیمی‌تر, the active one highlighted, and a ⋯ per row with
// rename (INLINE — a dialog to change four words is a dialog too many) and
// delete (confirmed, because the transcript goes with it).
//
// ── What is deliberately NOT in this list ────────────────────────────────
//
// The visits' copilot threads. They belong to the visit that opened them and
// reading them out of that context loses the only thing that made them make
// sense; the footer links to the record's «ویزیت‌ها» tab instead (core.md §9.5).
//
// The footer also carries the patient switcher and the balance line, because the
// decision a student makes at a composer is «آیا این را بپرسم؟» and the two
// numbers that answer it — the price and the balance — should be one glance
// apart.

export const SIDEBAR_WIDTH = 260;

/** One row. Renames in place; the ⋯ menu is anchored to the row's own button. */
function ThreadRow({ thread, active, onOpen, onRename, onDelete }) {
  const { t, formatDate } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const anchorRef = React.useRef(null);

  const title = threadTitle(thread, t('chat.thread.untitled'));
  // Relative, as §9.1 says — «همین حالا», «۵ دقیقه پیش», «دیروز» — and a date
  // once "N days ago" would read worse than the day itself.
  const when = relativeTimeOf(thread.lastMessageAtMs || thread.createdAtMs);
  const whenLabel = !when ? '' : when.date != null ? formatDate(when.date, 'dayMonth') : t(when.key, when.params);

  const commit = async () => {
    setEditing(false);
    // An EMPTY title restores the automatic one — that is the contract, not a
    // no-op, so an empty string is sent rather than swallowed (core.md §9.5).
    if (value.trim() !== title.trim()) await onRename?.(thread.id, value.trim());
  };

  if (editing) {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); commit(); }}
        style={{ padding: '4px 6px' }}
      >
        <input
          autoFocus
          value={value}
          aria-label={t('chat.thread.renamePlaceholder')}
          placeholder={t('chat.thread.renamePlaceholder')}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
          data-testid="thread-rename-input"
          style={{
            width: '100%', fontFamily: 'inherit', fontSize: 13,
            padding: '7px 9px', borderRadius: 8,
            border: `1px solid ${C.teal}`, color: C.textPrimary, background: C.card,
          }}
        />
      </form>
    );
  }

  return (
    <div
      className={HOVER_HOST}
      style={{
        display: 'flex', alignItems: 'center', gap: 2,
        borderRadius: 8,
        background: active ? soft(C.teal, 0.14) : 'transparent',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen?.(thread.id)}
        data-testid="thread-row"
        data-active={active ? '1' : '0'}
        style={{
          flex: 1, minWidth: 0, textAlign: 'start', cursor: 'pointer',
          background: 'transparent', border: 'none', font: 'inherit',
          padding: '8px 10px', color: active ? CT.accentInk : C.textPrimary,
        }}
      >
        <span
          style={{
            display: 'block', fontSize: 13, fontWeight: active ? 700 : 500,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        <span data-testid="thread-when" style={{ fontSize: 11, color: CT.mute }}>
          {whenLabel}
        </span>
      </button>
      <span ref={anchorRef} className={HOVER_ACTIONS}>
        <IconButton
          size={30}
          variant="ghost"
          icon={<MoreHorizontal size={15} aria-hidden />}
          label={t('chat.sidebar.menu')}
          onClick={() => setMenuOpen(true)}
          data-testid="thread-menu"
        />
      </span>
      <Menu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorRef={anchorRef}
        width={190}
        ariaLabel={t('chat.sidebar.menu')}
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => { setMenuOpen(false); setValue(title); setEditing(true); }}
          data-testid="thread-rename"
          style={ROW_STYLE}
        >
          <Pencil size={15} aria-hidden />
          {t('common.actions.rename')}
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={() => { setMenuOpen(false); onDelete?.(thread); }}
          data-testid="thread-delete"
          style={{ ...ROW_STYLE, color: CT.dangerInk }}
        >
          <Trash2 size={15} aria-hidden />
          {t('common.actions.delete')}
        </button>
      </Menu>
    </div>
  );
}

const ROW_STYLE = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '10px 12px',
  background: 'transparent', border: 'none', font: 'inherit',
  fontSize: 13, cursor: 'pointer', textAlign: 'start',
  color: 'inherit',
};

export default function ThreadSidebar({
  threads,
  loading = false,
  activeThreadId = null,
  balance = null,
  onNew,
  onOpen,
  onRename,
  onDelete,
  onSwitchPatient,
  onOpenVisits,
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const groups = React.useMemo(() => groupThreads(threads), [threads]);

  return (
    <nav
      data-testid="thread-sidebar"
      aria-label={t('chat.sidebar.title')}
      style={{
        display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
        background: C.bg, borderInlineEnd: `1px solid ${C.border}`,
      }}
    >
      <HoverRevealStyle />
      <div style={{ padding: 10 }}>
        <Button onClick={onNew} style={{ width: '100%' }} data-testid="thread-new">
          <Plus size={16} aria-hidden />
          {t('chat.sidebar.new')}
        </Button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 8px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <Spinner size={20} label={t('common.labels.loading')} />
          </div>
        ) : null}

        {!loading && groups.length === 0 ? (
          <p data-testid="thread-empty" style={{ fontSize: 12.5, color: CT.mute, padding: '8px 10px', lineHeight: 1.9 }}>
            {t('chat.sidebar.empty')}
          </p>
        ) : null}

        {groups.map((bucket) => (
          <section key={bucket.group}>
            <h3
              style={{
                fontSize: 11, fontWeight: 700, color: CT.mute,
                margin: '12px 10px 4px',
              }}
            >
              {t(dayGroupKey(bucket.group))}
            </h3>
            {bucket.threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                active={thread.id === activeThreadId}
                onOpen={onOpen}
                onRename={onRename}
                onDelete={setConfirming}
              />
            ))}
          </section>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: 10, display: 'grid', gap: 8 }}>
        <button type="button" onClick={onSwitchPatient} style={ROW_STYLE} data-testid="switch-patient">
          <Users size={15} aria-hidden />
          {t('chat.sidebar.otherPatient')}
        </button>
        <button type="button" onClick={onOpenVisits} style={ROW_STYLE} data-testid="visit-threads">
          <Stethoscope size={15} aria-hidden />
          {t('chat.sidebar.inVisit')}
        </button>
        <BalanceLine balance={balance} style={{ padding: '0 12px 2px' }} />
      </div>

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => { if (!open) setConfirming(null); }}
        title={t('chat.thread.deleteTitle')}
        description={t('chat.thread.deleteBody')}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={deleting}
        onConfirm={async () => {
          const target = confirming;
          setDeleting(true);
          try {
            await onDelete?.(target.id);
          } finally {
            setDeleting(false);
            setConfirming(null);
          }
        }}
        data-testid="thread-delete-confirm"
        confirmTestId="thread-delete-confirm-confirm"
        cancelTestId="thread-delete-confirm-cancel"
      />
    </nav>
  );
}
